import type { RuteroBookingLegDirection } from '@bandido/shared/types';
import { RUTERO_BOOKING_LEG_DIRECTIONS } from '@bandido/shared/types';

import type { BookingMoment } from './bookingMoment';
import type { ConnectorCapabilityVerdict } from './connectorCapabilityResolution';
import type {
  BookingExtra,
  PurchasedExtra,
  RuteroPassengerPayload,
} from './queries';

/**
 * Los servicios adicionales del proveedor, decididos en UN solo lugar.
 *
 * Los extras NO tienen canal en el hold (contrato del hub: `passengers[]` y
 * `extraId` viajan en `POST /bookings/{id}/extras`): se compran sobre la
 * reserva YA creada. Por eso viven en los momentos post-hold —Reservado y
 * Confirmado— y nunca en Revisar, donde no habría reserva sobre la cual
 * comprarlos.
 *
 * De acá salen las tres cosas que no pueden divergir entre las superficies que
 * los muestran (el modal del flujo y el detalle del listado): si la sección
 * EXISTE, a quién se le compra el extra, y qué viaja en el payload.
 */

/** Los momentos en que hay una reserva sobre la cual comprar un extra. */
const EXTRAS_MOMENTS: BookingMoment[] = ['reserved', 'confirmed'];

/**
 * Si la sección existe. Fail-CLOSED en las tres condiciones: sin un sí
 * explícito del conector la sección NO se dibuja —ni vacía ni con un aviso de
 * "no disponible"—, porque pedirle extras a un proveedor que no los publica
 * devuelve un error que se leería como falla nuestra.
 *
 * El id remoto es la otra mitad: sin reserva del otro lado no hay nada que
 * operar (el backend rechaza con 409), y un momento cerrado —cancelada o
 * fallida— tampoco es lugar para ofrecer una excursión sobre algo que ya no
 * existe.
 */
export function extrasSectionVisible({
  verdict,
  moment,
  hasRemoteBooking,
}: {
  verdict: ConnectorCapabilityVerdict;
  moment: BookingMoment;
  hasRemoteBooking: boolean;
}): boolean {
  return (
    hasRemoteBooking &&
    EXTRAS_MOMENTS.includes(moment) &&
    verdict === 'supported'
  );
}

export interface ExtraPassengerOption {
  /**
   * Índice 0-BASED en el orden en que los pasajeros viajaron al hold: es
   * exactamente lo que el contrato espera en `passengers[]`.
   */
  index: number;
  /** Nombre del REGISTRO; null cuando la fila no lo trae (ahí manda el número). */
  name: string | null;
}

/**
 * Los pasajeros a los que se le puede comprar el extra: los del REGISTRO
 * (`booking.passengers`), que son los que se enviaron al hold y en ese orden.
 *
 * Nunca los del formulario: el índice que viaja tiene que apuntar a la lista
 * que el hub conoce, y una reserva abierta desde el listado no tiene
 * formulario detrás. Sin lista —reservas anteriores al campo— no hay índice
 * que se pueda afirmar, y la consecuencia es explícita: sin selector y sin
 * `passengers` en el payload (= todos, por contrato).
 */
export function extraPassengerOptions(
  passengers: RuteroPassengerPayload[] | undefined,
): ExtraPassengerOption[] {
  if (!passengers) return [];
  return passengers.map((passenger, index) => {
    const name = `${passenger.firstName ?? ''} ${passenger.lastName ?? ''}`.trim();
    return { index, name: name.length > 0 ? name : null };
  });
}

/**
 * El selector se dibuja SOLO con más de un pasajero: con uno la elección no
 * existe (el extra es para él), y un chip único que no se puede desmarcar sin
 * bloquear el botón sería ruido.
 */
export function passengerSelectorVisible(
  options: readonly ExtraPassengerOption[],
): boolean {
  return options.length > 1;
}

/**
 * Los pasajeros elegidos, normalizados contra el registro: en el orden del
 * registro, sin repetidos y sin índices que la reserva no tenga. `undefined`
 * es "el asesor no tocó los chips" y son TODOS — el default marcado.
 */
export function resolveExtraPassengers({
  options,
  chosen,
}: {
  options: readonly ExtraPassengerOption[];
  chosen?: readonly number[];
}): number[] {
  if (!chosen) return options.map((option) => option.index);
  return options
    .filter((option) => chosen.includes(option.index))
    .map((option) => option.index);
}

/**
 * Un valor de vigencia que se puede comparar y mostrar como día de calendario.
 *
 * No alcanza con la FORMA: la vigencia es texto del proveedor, y un
 * "2026-13-01" con forma válida acotaría el picker con un límite imposible
 * —dejando el extra sin ninguna fecha comprable— y se dibujaría como "1 ene"
 * de otro año, que es peor que no dibujarlo. Se exige que el día exista de
 * verdad (ida y vuelta contra el calendario, que también atrapa los años
 * 0-99, que `Date` mapea a 1900-1999).
 */
function isCalendarDay(value: string | undefined): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

/**
 * Qué hace el POST con `date` para este extra — la declaración del contrato,
 * normalizada. `none` = el servicio ya tiene fecha (la del tramo, fijada por
 * el pickup): el POST la ignora y NO corresponde ofrecer un selector.
 * `optional` = día elegible dentro de la vigencia; sin fecha el hub usa
 * `validFrom`. `required` = sin fecha el POST responde 400.
 *
 * Ausente equivale a `none` (semántica cerrada del contrato), y una
 * declaración que no se reconoce se trata como NO declarada — no se le
 * inventa un selector a un valor que el contrato no define.
 *
 * De acá salen las TRES cosas que no pueden divergir: si el picker se dibuja
 * (`extraDatePickerVisible`), qué bloquea la compra (`extraAddBlock`) y si la
 * fecha viaja en el payload (`addExtraPayload`).
 */
export type ExtraDateSelection = 'none' | 'optional' | 'required';

export function extraDateSelection(
  extra: Pick<BookingExtra, 'dateSelection'>,
): ExtraDateSelection {
  const declared = extra.dateSelection;
  return declared === 'optional' || declared === 'required' ? declared : 'none';
}

/** Si el selector de fecha existe para este extra. */
export function extraDatePickerVisible(
  extra: Pick<BookingExtra, 'dateSelection'>,
): boolean {
  return extraDateSelection(extra) !== 'none';
}

/**
 * El tope de unidades que el operador acepta para este extra — la declaración
 * del contrato, normalizada: entero ≥ 0 (el 0 es real: no lo acepta), y
 * cualquier otra cosa —ausente, negativo, no entero— es NO declarado (null),
 * que no bloquea nada de este lado: el hub sigue rechazando con
 * `OPTIONAL_ITEM_LIMIT` antes de llamar al proveedor.
 */
export function extraMaxAllowed(
  extra: Pick<BookingExtra, 'maxAllowed'>,
): number | null {
  const declared = extra.maxAllowed;
  return typeof declared === 'number' &&
    Number.isInteger(declared) &&
    declared >= 0
    ? declared
    : null;
}

/**
 * Cuántas unidades quedan comprables de este extra: el `maxAllowed` menos lo
 * YA comprado (una fila de `purchased[]` = una unidad, y los `extraId` son
 * únicos dentro del booking, así que contar por id no cruza tramos). `null`
 * cuando el operador no declaró tope — sin tope afirmado no se bloquea.
 *
 * Es la MISMA cuenta para el bloqueo del botón y para el motivo que se lee al
 * lado (21.9): lo que deshabilita y lo que se explica no pueden divergir.
 */
export function extraRemainingAllowance({
  extra,
  purchased,
}: {
  extra: Pick<BookingExtra, 'extraId' | 'maxAllowed'>;
  purchased: readonly PurchasedExtra[];
}): number | null {
  const max = extraMaxAllowed(extra);
  if (max === null) return null;
  const bought = purchased.filter(
    (purchase) => purchase.extraId === extra.extraId,
  ).length;
  return Math.max(0, max - bought);
}

export interface ExtraAvailabilityPartition {
  actionable: BookingExtra[];
  unavailable: BookingExtra[];
}

/**
 * Se aparta solo lo definitivamente agotado. Los bloqueos corregibles
 * (pasajeros, fecha o cantidad) deben conservar sus controles visibles.
 */
export function partitionExtraAvailability({
  available,
  purchased,
}: {
  available: readonly BookingExtra[];
  purchased: readonly PurchasedExtra[];
}): ExtraAvailabilityPartition {
  return available.reduce<ExtraAvailabilityPartition>(
    (partition, extra) => {
      const bucket = extraRemainingAllowance({ extra, purchased }) === 0
        ? partition.unavailable
        : partition.actionable;
      bucket.push(extra);
      return partition;
    },
    { actionable: [], unavailable: [] },
  );
}

/**
 * Los límites del picker de fecha. Solo pasan los que son un día de calendario:
 * el contrato declara `validFrom`/`validTo` como "fechas/días del viaje" —
 * texto libre del proveedor—, y meter un valor que no es una fecha en el `min`
 * de un input lo deja sin límite alguno sin que nadie se entere.
 */
export function extraDateBounds(extra: Pick<BookingExtra, 'validFrom' | 'validTo'>): {
  min?: string;
  max?: string;
} {
  return {
    ...(isCalendarDay(extra.validFrom) ? { min: extra.validFrom } : {}),
    ...(isCalendarDay(extra.validTo) ? { max: extra.validTo } : {}),
  };
}

/**
 * La vigencia del extra tal como se lee en la fila ("Válido 19 sep – 3 oct").
 *
 * Un extremo que NO es un día de calendario se muestra CRUDO: el contrato
 * declara `validFrom`/`validTo` como "fechas/días del viaje" y es texto del
 * proveedor, así que inventarle una fecha sería peor que mostrar lo que dijo.
 * Con un solo extremo publicado, el otro queda como guion — la vigencia
 * incompleta es la que hay. Sin ninguno no hay vigencia que mostrar.
 */
export function extraValidityLabel(
  extra: Pick<BookingExtra, 'validFrom' | 'validTo'>,
  locale = 'es',
): { from: string; to: string } | null {
  if (!extra.validFrom && !extra.validTo) return null;
  return {
    from: extra.validFrom ? calendarDayLabel(extra.validFrom, locale) : '—',
    to: extra.validTo ? calendarDayLabel(extra.validTo, locale) : '—',
  };
}

/**
 * La fecha se arma en hora LOCAL (`new Date(y, m - 1, d)`): parsear
 * "2026-09-19" con `new Date(string)` la interpreta en UTC y al oeste de
 * Greenwich se pinta el día anterior.
 */
function calendarDayLabel(value: string, locale: string): string {
  if (!isCalendarDay(value)) return value;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
  });
}

/**
 * Lo que impide comprar el extra, o null. Es UNA expresión: de acá salen el
 * `disabled` del botón y el motivo que se lee al lado — nunca un gris mudo.
 */
export type ExtraAddBlock =
  | 'noPassenger'
  | 'dateOutOfRange'
  | 'dateRequired'
  | 'limitReached'
  | 'overRemaining';

export function extraAddBlock({
  extra,
  date,
  passengers,
  options,
  purchased,
}: {
  extra: Pick<
    BookingExtra,
    'extraId' | 'validFrom' | 'validTo' | 'dateSelection' | 'maxAllowed'
  >;
  date?: string;
  /** Los ya resueltos por `resolveExtraPassengers`. */
  passengers: readonly number[];
  options: readonly ExtraPassengerOption[];
  /** Lo YA comprado sobre el booking (las filas del server). */
  purchased: readonly PurchasedExtra[];
}): ExtraAddBlock | null {
  // El cupo del operador primero: una fila sin cupo (maxAllowed 0, o ya
  // agotado por compras) está muerta y no hay chips ni fecha que la revivan.
  const remaining = extraRemainingAllowance({ extra, purchased });
  if (remaining !== null && remaining === 0) return 'limitReached';
  if (passengerSelectorVisible(options) && passengers.length === 0) {
    return 'noPassenger';
  }
  // La compra es "una unidad por pasajero": los elegidos son las unidades que
  // van a viajar. Sin registro de pasajeros no se puede afirmar cuántas compra
  // el proveedor (el payload dice "todos"), así que se afirma el mínimo (1) y
  // el resto lo valida el hub — igual que siempre.
  const requested = options.length === 0 ? 1 : passengers.length;
  if (remaining !== null && requested > remaining) return 'overRemaining';
  const selection = extraDateSelection(extra);
  // Con `none` la fecha no existe para este extra: el POST la ignora, el
  // picker no se dibuja, y un borrador viejo no puede bloquear la compra.
  if (selection === 'none') return null;
  const chosen = date?.trim();
  if (!chosen) {
    return selection === 'required' ? 'dateRequired' : null;
  }
  const { min, max } = extraDateBounds(extra);
  // Los días de calendario ISO ordenan lexicográficamente: comparar strings
  // es exacto y no arrastra zonas horarias.
  if (isCalendarDay(chosen) && ((min && chosen < min) || (max && chosen > max))) {
    return 'dateOutOfRange';
  }
  // Una fecha que ni siquiera es un día no se puede contrastar contra la
  // vigencia. No se bloquea acá: la valida el borde (el DTO exige un día de
  // calendario real) y el proveedor.
  return null;
}

export interface AddExtraPayload {
  extraId: string;
  date?: string;
  passengers?: number[];
}

/**
 * El payload EXACTO del `POST .../extras`.
 *
 * `passengers` se OMITE cuando la compra cubre a todos: el contrato define
 * "sin indicar = todos", así que mandar la lista completa y omitirla son la
 * misma compra, y omitirla conserva tal cual lo que se venía enviando (ningún
 * extra existente cambia de significado por este cambio). Se omite también sin
 * registro de pasajeros, que es el único caso en que no se puede afirmar a qué
 * apunta un índice.
 *
 * `date` viaja solo cuando el extra la declara elegible (`dateSelection`
 * optional/required): con `none` el contrato la ignora, y un borrador viejo
 * no debe viajar como si significara algo.
 */
export function addExtraPayload({
  extra,
  date,
  passengers,
  options,
}: {
  extra: Pick<BookingExtra, 'extraId' | 'dateSelection'>;
  date?: string;
  /** Los ya resueltos por `resolveExtraPassengers`. */
  passengers: readonly number[];
  options: readonly ExtraPassengerOption[];
}): AddExtraPayload {
  const chosen =
    extraDateSelection(extra) === 'none' ? undefined : date?.trim();
  const everyone =
    options.length === 0 || passengers.length === options.length;
  return {
    extraId: extra.extraId,
    ...(chosen ? { date: chosen } : {}),
    ...(everyone ? {} : { passengers: [...passengers] }),
  };
}

/**
 * De quién es una compra ya hecha.
 *
 * Importa porque una compra es CANCELABLE: el proveedor devuelve una fila por
 * pasajero y todas se leen iguales —mismo nombre, misma fecha, mismo precio—,
 * así que sin decir de quién es cada una el botón de quitar borra la de
 * cualquiera. Son estados distintos y no se colapsan en un blanco:
 *
 * - `passenger`   — la reserva tiene UN pasajero: la compra no puede ser de
 *   otro, y se lo nombra a él en vez del id opaco del proveedor.
 * - `reference`   — el proveedor publicó su `passengerRef`. Es un id SUYO y no
 *   hay mapeo publicado contra nuestro registro (su `Booking` no trae
 *   pasajeros), así que se muestra tal cual: distingue las filas entre sí, que
 *   es exactamente lo que hace falta para no cancelar la equivocada.
 * - `unspecified` — hay varios pasajeros y el proveedor no dice de quién es.
 *   Se dice ASÍ, en vez de dejar dos filas idénticas que se lean como
 *   intercambiables.
 * - `none`        — sin registro de pasajeros y sin ref: no hay nada que
 *   afirmar.
 */
export type PurchasedExtraIdentity =
  | { kind: 'passenger'; index: number }
  | { kind: 'reference'; reference: string }
  | { kind: 'unspecified' }
  | { kind: 'none' };

export interface PurchasedExtraRow {
  purchase: PurchasedExtra;
  identity: PurchasedExtraIdentity;
}

/**
 * Las compras con SU identidad al lado, en una sola pasada: la fila que se
 * dibuja y el `purchaseId` que cancela el botón salen del MISMO objeto, así
 * que no puede quedar un nombre pegado al botón que borra otra compra.
 */
export function purchasedExtraRows({
  purchased,
  options,
}: {
  purchased: readonly PurchasedExtra[];
  options: readonly ExtraPassengerOption[];
}): PurchasedExtraRow[] {
  return purchased.map((purchase) => ({
    purchase,
    identity: purchasedExtraIdentity({
      passengerRef: purchase.passengerRef,
      options,
    }),
  }));
}

export interface PurchasedExtraOrigin {
  providerAdded: boolean;
  free: boolean;
}

/**
 * Presentación de cada compra ACTUAL que devuelve `/extras`. El contrato no
 * enlaza `purchaseId` con el snapshot del hold: compartir `extraId` solo dice
 * que usan la misma entrada del catálogo y una cancelación + recompra puede
 * reutilizarla. Por eso esta fila nunca hereda procedencia ni precio
 * históricos. La confirmación original se muestra en su bloque autoritativo;
 * acá "Sin costo" exige un cero explícito de ESTA compra.
 */
export function purchasedExtraOrigins({
  purchased,
}: {
  purchased: readonly PurchasedExtra[];
}): Map<string, PurchasedExtraOrigin> {
  return new Map(
    purchased.map((purchase) => [
      purchase.purchaseId,
      {
        providerAdded: false,
        free: purchase.price?.amountMinor === 0,
      },
    ]),
  );
}

export function purchasedExtraIdentity({
  passengerRef,
  options,
}: {
  passengerRef?: string;
  options: readonly ExtraPassengerOption[];
}): PurchasedExtraIdentity {
  if (options.length === 1) return { kind: 'passenger', index: options[0].index };
  const reference = passengerRef?.trim();
  if (reference) return { kind: 'reference', reference };
  if (options.length > 1) return { kind: 'unspecified' };
  return { kind: 'none' };
}

/**
 * Lo tipeado y lo marcado, ATADO a la reserva que lo produjo.
 *
 * Las dos pantallas que dibujan la sección cambian de reserva SIN desmontarse
 * (el detalle del listado salta de una fila a otra), y los ids de extra son
 * del proveedor: el mismo `extraId` aparece en dos reservas suyas. Un borrador
 * que sobreviva a ese cambio compraría la excursión para los pasajeros que
 * eligió el asesor en OTRA reserva — un subset plausible y equivocado, que es
 * peor que ninguno.
 */
export interface ExtraDrafts {
  /** La reserva a la que pertenece lo tipeado. */
  bookingId: string;
  /** Fecha del servicio elegida, por extra. */
  dates: Record<string, string>;
  /** Pasajeros marcados, por extra. Ausente = nadie los tocó = todos. */
  passengers: Record<string, number[]>;
}

export function emptyExtraDrafts(bookingId: string): ExtraDrafts {
  return { bookingId, dates: {}, passengers: {} };
}

/** Los borradores de ESTA reserva; los de otra no se leen, se descartan. */
export function extraDraftsFor(
  drafts: ExtraDrafts,
  bookingId: string,
): ExtraDrafts {
  return drafts.bookingId === bookingId ? drafts : emptyExtraDrafts(bookingId);
}

/**
 * Cómo se lee el precio publicado del extra.
 *
 * El contrato NO declara si el importe es por pasajero o por reserva; lo que
 * sí declara es que la compra es "una unidad por pasajero" y que cada fila
 * comprada lleva su `passengerRef`. Entonces, cuando hay a quién elegir, el
 * número publicado describe UNA unidad y se dice así, con la cantidad de
 * pasajeros elegidos al lado. **No se multiplica nada**: el frontend formatea,
 * y el importe autoritativo de lo comprado son las filas que devuelve el
 * server.
 */
export function extraPriceNote({
  options,
  passengers,
}: {
  options: readonly ExtraPassengerOption[];
  passengers: readonly number[];
}): { passengers: number } | null {
  if (!passengerSelectorVisible(options)) return null;
  return { passengers: passengers.length };
}

/**
 * El catálogo y lo comprado, agrupados por el TRAMO dueño.
 *
 * En verticales multi-tramo (transfers) cada extra llega con su `legId`
 * (el mismo id opaco que `booking.legs[]`) y el catálogo es POR tramo: el
 * mismo servicio aparece una vez por tramo con su propio `extraId`, y
 * comprarlo para la ida no lo compra para la vuelta. Sin agrupar, el asesor
 * ve la lista duplicada y no puede saber a cuál tramo le agrega — y el
 * "ya comprado" tampoco dice de qué tramo es.
 *
 * - La identidad del grupo es el `legId`; `direction` es su rótulo (puede
 *   faltar en un contrato degradado: el grupo existe igual, sin rótulo).
 * - Los extras SIN tramo van a un único grupo sin rótulo — el comportamiento
 *   de siempre para los proveedores que no distinguen tramos.
 * - El orden es el de PRIMERA aparición en `available[]` (el contrato lista
 *   el catálogo de cada tramo en el orden en que el proveedor lista los
 *   journeys) y después el de `purchased[]`, para un grupo que solo tiene
 *   compras. Nada se reordena.
 */
export interface ExtrasLegGroup {
  /** El tramo dueño; null = extras sin tramo (proveedor que no distingue). */
  legId: string | null;
  /** El rótulo del tramo; null cuando el contrato no lo publica. */
  direction: RuteroBookingLegDirection | null;
  available: BookingExtra[];
  purchased: PurchasedExtra[];
}

function extraLegDirection(
  declared: string | undefined,
): RuteroBookingLegDirection | null {
  return (RUTERO_BOOKING_LEG_DIRECTIONS as readonly string[]).includes(
    declared ?? '',
  )
    ? (declared as RuteroBookingLegDirection)
    : null;
}

export function extrasLegGroups({
  available,
  purchased,
}: {
  available: readonly BookingExtra[];
  purchased: readonly PurchasedExtra[];
}): ExtrasLegGroup[] {
  const groups = new Map<string, ExtrasLegGroup>();
  const groupFor = (
    rawLegId: string | undefined,
    rawDirection: string | undefined,
  ): ExtrasLegGroup => {
    const legId = rawLegId?.trim() || null;
    const key = legId ?? '';
    let group = groups.get(key);
    if (!group) {
      group = { legId, direction: null, available: [], purchased: [] };
      groups.set(key, group);
    }
    // El rótulo sale de la primera fila del grupo que lo declare: las filas
    // de un mismo tramo no pueden contradecirse (mismo legId ⇒ misma
    // direction, garantía del contrato).
    if (!group.direction) group.direction = extraLegDirection(rawDirection);
    return group;
  };
  for (const extra of available) {
    groupFor(extra.legId, extra.direction).available.push(extra);
  }
  for (const purchase of purchased) {
    groupFor(purchase.legId, purchase.direction).purchased.push(purchase);
  }
  const priority: Record<RuteroBookingLegDirection, number> = {
    outbound: 0,
    return: 1,
  };
  // El proveedor puede devolver la vuelta primero. La pantalla conserva las
  // filas dentro de cada tramo, pero ordena los grupos por sentido semántico.
  return [...groups.values()].sort(
    (left, right) =>
      (left.direction ? priority[left.direction] : 2) -
      (right.direction ? priority[right.direction] : 2),
  );
}
